import { Modal, Spin, Progress, Typography, Tag } from "antd";

const { Text } = Typography;

/**
 * AnalyticsProgressModal - Specialized modal for analytics processing
 * props:
 *  - processing: bool (analytics processing phase)
 *  - activeStep: number (1..totalSteps)
 *  - totalSteps: number (defaults to 4)
 *  - onClose: fn
 *  - disableClose: bool
 *  - modalSelection: string (selected model name)
 *  - dependentVariable: string (selected dependent variable)
 *  - groupBy: array (selected group by columns)
 *  - MODEL_OPTIONS: array (available model options)
 */
const AnalyticsProgressModal = ({
  processing = false,
  activeStep: activeStepProp,
  totalSteps = 4,
  onClose,
  disableClose = true,
  modalSelection,
  dependentVariable,
  groupBy = [],
  MODEL_OPTIONS = [],
}) => {
  const visible = !!processing;

  // derive active step: prefer prop, else 1 for processing start
  let activeStep = typeof activeStepProp === "number" ? activeStepProp : 1;

  const percent = processing
    ? Math.round((Math.min(Math.max(activeStep, 1), totalSteps) / totalSteps) * 100)
    : 0;

  const handleCancel = () => {
    if (disableClose) return;
    if (typeof onClose === "function") onClose();
  };

  // analytics-specific step messages
  const stepMessages = [
    "Initializing Analytics Engine",
    "Processing Selected Model Configuration",
    "Applying Data Transformations",
    "Generating Analytics Results",
  ];

  // Get model display name
  const getModelName = () => {
    const model = MODEL_OPTIONS?.find((opt) => opt?.value === modalSelection);
    return model?.label || String(modalSelection || 'Unknown Model');
  };

  return (
    <Modal
      title="Analytics Processing"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      centered
      maskClosable={!disableClose}
      closable={!disableClose}
      width={600}
    >
      <div style={{ textAlign: "center" }}>
        {processing && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin  tip="Processing analytics..." />
            </div>

            <Progress
              percent={percent}
              status={activeStep < totalSteps ? "active" : "normal"}
              style={{ marginBottom: 12 }}
            />

            <div style={{ marginBottom: 12 }}>
              Step {Math.min(activeStep, totalSteps)} of {totalSteps}
            </div>


            {/* Dynamic Configuration Display */}
            <div style={{
              marginTop: 16,
              padding: 16,
              borderRadius: 8,
              background: "rgba(0,0,0,0.04)",
              textAlign: "left"
            }}>
              <Text strong style={{ fontSize: 16, marginBottom: 12, display: "block" }}>
                Executing Selected Configuration
              </Text>
              
              <div style={{ marginBottom: 8 }}>
                <Text strong>✨ Model Chosen:</Text>
                <span style={{ marginLeft: 8, fontWeight: 500 }}>
                  {getModelName()}
                </span>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <Text strong>🧙 Applying DBSCAN sorcery on :</Text>
                <span style={{ marginLeft: 8, fontWeight: 500 }}>
                  {dependentVariable || '-'}
                </span>
              </div>
              
              <div>
                <Text strong>🔮 Clustering Columns:</Text>
                <div style={{ marginTop: 4 }}>
                  {Array.isArray(groupBy) && groupBy.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {groupBy.map((g) => (
                        <Tag key={g} color="blue" style={{ borderRadius: 12, padding: "2px 8px" }}>
                          {g}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">None</Text>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AnalyticsProgressModal;
